import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Pagination,
  CircularProgress,
  Grid,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

const TiposAtendimentoPanel = () => {
  const [tiposAtendimento, setTiposAtendimento] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    total_paginas: 1,
    total_registros: 0,
    registros_por_pagina: 50
  });
  const [filtros, setFiltros] = useState({
    tipoAtendimento: ''
  });
  const [ordenacao, setOrdenacao] = useState({
    order_by: 'id',
    order_direction: 'asc'
  });
  const [formData, setFormData] = useState({
    TipoAtendimento: '',
  });

  const fetchTiposAtendimento = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      
      // Usar ordenação customizada ou a atual
      const ordenacaoAtual = customOrdenacao || ordenacao;
      
      // Construir query string com filtros e ordenação
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        order_by: ordenacaoAtual.order_by,
        order_direction: ordenacaoAtual.order_direction
      });
      
      // Adicionar filtros se preenchidos
      if (filtros.tipoAtendimento) params.append('tipoAtendimento', filtros.tipoAtendimento);
      
      const response = await api.get(`/tipos-atendimento?${params}`);
      
      console.log('Resposta da API de tipos de atendimento:', response.data);
      
      // Verificar se a resposta tem estrutura de paginação
      if (response.data.dados && response.data.paginacao) {
        setTiposAtendimento(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        // Fallback para formato simples (array direto)
        let dados = [];
        if (Array.isArray(response.data)) {
          dados = response.data;
        } else if (response.data && typeof response.data === 'object') {
          const possibleArray = Object.values(response.data).find(value => Array.isArray(value));
          if (possibleArray) {
            dados = possibleArray;
          }
        }
        
        // Aplicar filtros localmente se não houver paginação no backend
        if (filtros.tipoAtendimento) {
          dados = dados.filter(item => 
            item.TipoAtendimento && item.TipoAtendimento.toLowerCase().includes(filtros.tipoAtendimento.toLowerCase())
          );
        }
        
        // Aplicar ordenação localmente
        dados.sort((a, b) => {
          let aValue, bValue;
          if (ordenacaoAtual.order_by === 'id') {
            aValue = a.id;
            bValue = b.id;
          } else {
            aValue = a.TipoAtendimento || '';
            bValue = b.TipoAtendimento || '';
          }
          
          if (ordenacaoAtual.order_direction === 'desc') {
            return bValue > aValue ? 1 : -1;
          } else {
            return aValue > bValue ? 1 : -1;
          }
        });
        
        // Simular paginação local
        const totalRegistros = dados.length;
        const registrosPorPagina = 50;
        const totalPaginas = Math.ceil(totalRegistros / registrosPorPagina);
        const inicio = (page - 1) * registrosPorPagina;
        const fim = inicio + registrosPorPagina;
        const dadosPaginados = dados.slice(inicio, fim);
        
        setTiposAtendimento(dadosPaginados);
        setPaginacao({
          pagina_atual: page,
          total_paginas: totalPaginas,
          total_registros: totalRegistros,
          registros_por_pagina: registrosPorPagina
        });
      }
    } catch (error) {
      console.error('Erro ao buscar tipos de atendimento:', error.response || error);
      setError('Erro ao carregar tipos de atendimento. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]);

  useEffect(() => {
    fetchTiposAtendimento(1);
  }, [fetchTiposAtendimento]);

  const handlePageChange = (event, value) => {
    fetchTiposAtendimento(value);
  };

  const handleSort = (campo) => {
    const novaOrdenacao = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc'
    };
    setOrdenacao(novaOrdenacao);
    fetchTiposAtendimento(1, novaOrdenacao);
  };

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    fetchTiposAtendimento(1);
  };

  const limparFiltros = () => {
    setFiltros({
      tipoAtendimento: ''
    });
    // Após limpar, buscar novamente
    setTimeout(() => {
      fetchTiposAtendimento(1);
    }, 100);
  };

  const handleSubmit = async () => {
    try {
      // Validar dados obrigatórios
      if (!formData.TipoAtendimento || !formData.TipoAtendimento.trim()) {
        setError('Tipo de atendimento é obrigatório');
        return;
      }

      // Transformar os dados para o formato esperado pelo backend
      const dadosParaEnvio = {
        TipoAtendimento: formData.TipoAtendimento.trim()
      };

      if (editingTipo) {
        await api.put(`/tipos-atendimento/${editingTipo.id}`, dadosParaEnvio);
        setSuccess('Tipo de atendimento atualizado com sucesso!');
      } else {
        await api.post('/tipos-atendimento', dadosParaEnvio);
        setSuccess('Tipo de atendimento adicionado com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingTipo(null);
      setFormData({ TipoAtendimento: '' });
      fetchTiposAtendimento(paginacao.pagina_atual);
      
      // Limpar mensagens após 3 segundos
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar tipo de atendimento:', error);
      const mensagem = error.response?.data?.message || 'Erro ao salvar. Por favor, verifique os dados e tente novamente.';
      setError(mensagem);
    }
  };

  const handleEdit = (tipo) => {
    setEditingTipo(tipo);
    setFormData({ TipoAtendimento: tipo.TipoAtendimento || '' });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este tipo de atendimento?')) {
      try {
        await api.delete(`/tipos-atendimento/${id}`);
        setSuccess('Tipo de atendimento excluído com sucesso!');
        fetchTiposAtendimento(paginacao.pagina_atual);
        
        // Limpar a mensagem de sucesso após 3 segundos
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (error) {
        console.error('Erro ao excluir tipo de atendimento:', error);
        const mensagem = error.response?.data?.message || 'Erro ao excluir. Por favor, tente novamente.';
        setError(mensagem);
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingTipo(null);
    setFormData({ TipoAtendimento: '' });
    setError('');
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Gerenciamento de Tipos de Atendimento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cadastre e gerencie os tipos de atendimento
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setEditingTipo(null);
              setFormData({ TipoAtendimento: '' });
              setError('');
              setOpenDialog(true);
            }}
            startIcon={<AddIcon />}
          >
            Novo Tipo de Atendimento
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filtros Expansíveis */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
              <Typography variant="h6">
                Filtros
              </Typography>
              <Button
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant="outlined"
                size="small"
              >
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </Button>
            </Box>
            
            <Collapse in={showFilters}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Tipo de Atendimento"
                    value={filtros.tipoAtendimento}
                    onChange={(e) => handleFilterChange('tipoAtendimento', e.target.value)}
                    fullWidth
                    size="small"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        aplicarFiltros();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                    <Button
                      variant="contained"
                      onClick={aplicarFiltros}
                      size="small"
                    >
                      Aplicar Filtros
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={limparFiltros}
                      startIcon={<ClearIcon />}
                      size="small"
                    >
                      Limpar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </Card>

        {/* Tabela com Loading */}
        <TableContainer component={Paper} sx={{ mt: 3, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={ordenacao.order_by === 'id'}
                    direction={ordenacao.order_by === 'id' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'TipoAtendimento'}
                    direction={ordenacao.order_by === 'TipoAtendimento' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('TipoAtendimento')}
                  >
                    Tipo de Atendimento
                  </TableSortLabel>
                </TableCell>
                <TableCell width="120">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tiposAtendimento.length > 0 ? (
                tiposAtendimento.map((tipo) => (
                  <TableRow key={tipo.id} hover>
                    <TableCell>{tipo.id}</TableCell>
                    <TableCell>{tipo.TipoAtendimento}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEdit(tipo)}
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDelete(tipo.id)}
                        title="Excluir"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    {loading ? 'Carregando...' : 'Nenhum registro encontrado'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Informações de Paginação e Controles */}
        {paginacao.total_registros > 0 && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {Math.min((paginacao.pagina_atual - 1) * paginacao.registros_por_pagina + 1, paginacao.total_registros)} - {Math.min(paginacao.pagina_atual * paginacao.registros_por_pagina, paginacao.total_registros)} de {paginacao.total_registros} registros
            </Typography>
            
            {paginacao.total_paginas > 1 && (
              <Pagination
                count={paginacao.total_paginas}
                page={paginacao.pagina_atual}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}
          </Box>
        )}

        {/* Dialog para Criar/Editar */}
        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTipo ? 'Editar Tipo de Atendimento' : 'Novo Tipo de Atendimento'}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Tipo de Atendimento"
              name="TipoAtendimento"
              value={formData.TipoAtendimento}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              error={!!error && !formData.TipoAtendimento.trim()}
              helperText={error && !formData.TipoAtendimento.trim() ? 'Campo obrigatório' : ''}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancelar</Button>
            <Button 
              onClick={handleSubmit} 
              color="primary" 
              variant="contained"
              disabled={!formData.TipoAtendimento.trim()}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default TiposAtendimentoPanel; 